/**
 * Figure — figure géométrique cotée pour les questions de mesures, aires, volumes et échelles (SVG, sans dépendance).
 *
 * data: {
 *   title: "Plan du terrain",                      // facultatif
 *   shape: 'rect' | 'right_triangle' | 'triangle' | 'circle' | 'polygon' | 'cuboid' | 'cylinder',
 *   // champs au même niveau que shape ; dimensions dans une unité de dessin quelconque (seules les proportions comptent) :
 *   rect:            w, h, labels: { w: "8 m", h: "5 m" }
 *   right_triangle:  base, height, labels: { base, height, hyp }        // angle droit en bas à gauche
 *   triangle:        base, height, apex (0..1), labels: { base, height } // hauteur en pointillé
 *   circle:          r, show: 'radius' | 'diameter', label: "r = 3 cm"
 *   polygon:         points: [[x, y], …], edges: ["12 m", null, …]     // y vers le haut ; edges[i] = côté i → i+1
 *   cuboid:          l, w, h, labels: { l, w, h }                       // pavé droit en perspective cavalière
 *   cylinder:        r, h, labels: { r, h }
 *   scale_bar: { length, label: "50 m" },          // facultatif : barre d'échelle (length en unités de dessin)
 *   note: "Le dessin n'est pas à l'échelle."         // facultatif, sous la figure
 * }
 * Les cotes sont écrites sur la figure : la question porte sur le calcul, pas sur une mesure à la règle.
 */

const W = 400;
const H = 260;
const MARGE = 34;
const MARGE_X = 62; // place pour les cotes des côtés verticaux (« 30 cm »), sinon rognées au bord

// Ajuste un ensemble de points (unités de dessin, y vers le haut) dans le cadre SVG, proportions conservées.
function cadre(points, extraBas = 0) {
    const xs = points.map(p => p[0]);
    const ys = points.map(p => p[1]);
    const [x0, x1, y0, y1] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
    const k = Math.min((W - 2 * MARGE_X) / ((x1 - x0) || 1), (H - 2 * MARGE - extraBas) / ((y1 - y0) || 1));
    const dx = (W - (x1 - x0) * k) / 2;
    const dy = (H - extraBas - (y1 - y0) * k) / 2;
    return { k, P: ([x, y]) => [dx + (x - x0) * k, H - extraBas - dy - (y - y0) * k] };
}

// Cote au milieu d'un segment, décalée vers l'extérieur de la figure (centre c).
function Cote({ a, b, texte, c, ecart = 14 }) {
    if (!texte) return null;
    const [mx, my] = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
    let [nx, ny] = [-(b[1] - a[1]), b[0] - a[0]];
    const n = Math.hypot(nx, ny) || 1;
    [nx, ny] = [nx / n, ny / n];
    if ((mx - c[0]) * nx + (my - c[1]) * ny < 0) [nx, ny] = [-nx, -ny];
    // Côté plutôt vertical : texte calé à gauche ou à droite du trait (un ancrage centré le chevauchait).
    if (Math.abs(nx) > 0.8) {
        return (
            <text x={mx + nx * 7} y={my + 4} textAnchor={nx > 0 ? 'start' : 'end'} className="qv-figure__cote">{texte}</text>
        );
    }
    return (
        <text x={mx + nx * ecart} y={my + ny * ecart + 4} textAnchor="middle" className="qv-figure__cote">{texte}</text>
    );
}

const centre = pts => [pts.reduce((s, p) => s + p[0], 0) / pts.length, pts.reduce((s, p) => s + p[1], 0) / pts.length];

function Polygone({ pts, edges = [], extra = null }) {
    const c = centre(pts);
    return (
        <g>
            <polygon points={pts.map(p => p.join(',')).join(' ')} className="qv-figure__forme" />
            {extra}
            {pts.map((p, i) => <Cote key={i} a={p} b={pts[(i + 1) % pts.length]} texte={edges[i]} c={c} />)}
        </g>
    );
}

function AngleDroit({ o, a, b, t = 10 }) {
    const u = (p) => { const d = Math.hypot(p[0] - o[0], p[1] - o[1]) || 1; return [(p[0] - o[0]) / d * t, (p[1] - o[1]) / d * t]; };
    const [ua, ub] = [u(a), u(b)];
    return <polyline points={`${o[0] + ua[0]},${o[1] + ua[1]} ${o[0] + ua[0] + ub[0]},${o[1] + ua[1] + ub[1]} ${o[0] + ub[0]},${o[1] + ub[1]}`} className="qv-figure__angle" />;
}

function dessin(d, extraBas) {
    const L = d.labels || {};
    switch (d.shape) {
        case 'rect': {
            const { P } = cadre([[0, 0], [d.w, d.h]], extraBas);
            const pts = [[0, 0], [d.w, 0], [d.w, d.h], [0, d.h]].map(P);
            return <Polygone pts={pts} edges={[L.w, L.h, null, null]} />;
        }
        case 'right_triangle': {
            const { P } = cadre([[0, 0], [d.base, d.height]], extraBas);
            const pts = [[0, 0], [d.base, 0], [0, d.height]].map(P);
            return <Polygone pts={pts} edges={[L.base, L.hyp, L.height]} extra={<AngleDroit o={pts[0]} a={pts[1]} b={pts[2]} />} />;
        }
        case 'triangle': {
            const ax = (typeof d.apex === 'number' ? d.apex : 0.4) * d.base;
            const { P } = cadre([[0, 0], [d.base, 0], [ax, d.height]], extraBas);
            const pts = [[0, 0], [d.base, 0], [ax, d.height]].map(P);
            const pied = P([ax, 0]);
            const hauteur = (
                <g>
                    <line x1={pts[2][0]} y1={pts[2][1]} x2={pied[0]} y2={pied[1]} className="qv-figure__aux" />
                    <AngleDroit o={pied} a={pts[1]} b={pts[2]} t={8} />
                    {L.height && <text x={pied[0] + 6} y={(pts[2][1] + pied[1]) / 2 + 4} className="qv-figure__cote">{L.height}</text>}
                </g>
            );
            return <Polygone pts={pts} edges={[L.base, null, null]} extra={hauteur} />;
        }
        case 'circle': {
            const r = Math.min(W - 2 * MARGE, H - 2 * MARGE - extraBas) / 2;
            const [cx, cy] = [W / 2, (H - extraBas) / 2];
            const diam = d.show === 'diameter';
            return (
                <g>
                    <circle cx={cx} cy={cy} r={r} className="qv-figure__forme" />
                    <circle cx={cx} cy={cy} r={2.5} className="qv-figure__point" />
                    <line x1={diam ? cx - r : cx} y1={cy} x2={cx + r} y2={cy} className="qv-figure__trait" />
                    {d.label && <text x={diam ? cx : cx + r / 2} y={cy - 7} textAnchor="middle" className="qv-figure__cote">{d.label}</text>}
                </g>
            );
        }
        case 'polygon': {
            const brut = (d.points || []).filter(p => Array.isArray(p) && p.length === 2);
            if (brut.length < 3) return null;
            const { P } = cadre(brut, extraBas);
            return <Polygone pts={brut.map(P)} edges={d.edges || []} />;
        }
        case 'cuboid': {
            // Perspective cavalière : fuyante à 45°, coefficient 0,5.
            const f = [d.w * 0.5 * Math.SQRT1_2, d.w * 0.5 * Math.SQRT1_2];
            const avant = [[0, 0], [d.l, 0], [d.l, d.h], [0, d.h]];
            const arriere = avant.map(p => [p[0] + f[0], p[1] + f[1]]);
            const { P } = cadre([...avant, ...arriere], extraBas);
            const [a, b] = [avant.map(P), arriere.map(P)];
            const c = centre([...a, ...b]);
            return (
                <g>
                    <polygon points={a.map(p => p.join(',')).join(' ')} className="qv-figure__forme" />
                    <polygon points={[a[3], b[3], b[2], a[2]].map(p => p.join(',')).join(' ')} className="qv-figure__forme qv-figure__forme--face" />
                    <polygon points={[a[1], b[1], b[2], a[2]].map(p => p.join(',')).join(' ')} className="qv-figure__forme qv-figure__forme--face" />
                    <polyline points={[a[0], b[0], b[1]].map(p => p.join(',')).join(' ')} className="qv-figure__cache" />
                    <line x1={b[0][0]} y1={b[0][1]} x2={b[3][0]} y2={b[3][1]} className="qv-figure__cache" />
                    <Cote a={a[0]} b={a[1]} texte={L.l} c={c} />
                    <Cote a={a[1]} b={b[1]} texte={L.w} c={c} ecart={16} />
                    <Cote a={a[0]} b={a[3]} texte={L.h} c={c} ecart={18} />
                </g>
            );
        }
        case 'cylinder': {
            // Proportions respectées : un cylindre plus haut que large est rétréci plutôt que tassé (27/10).
            const ratio = (d.h || 1) / ((d.r || 1) * 2); // hauteur / diamètre
            const dispo = H - 2 * MARGE - extraBas;
            let rx = Math.min(80, (W - 2 * MARGE) / 2);
            if (rx * 2 * ratio + 2 * rx * 0.28 > dispo) rx = dispo / (2 * ratio + 0.56);
            const ry = rx * 0.28;
            const haut = rx * 2 * ratio;
            const [cx, yh] = [W / 2, (H - extraBas - haut) / 2];
            const yb = yh + haut;
            return (
                <g>
                    <path d={`M ${cx - rx} ${yh} L ${cx - rx} ${yb} A ${rx} ${ry} 0 0 0 ${cx + rx} ${yb} L ${cx + rx} ${yh}`} className="qv-figure__forme" />
                    <ellipse cx={cx} cy={yh} rx={rx} ry={ry} className="qv-figure__forme qv-figure__forme--face" />
                    <path d={`M ${cx - rx} ${yb} A ${rx} ${ry} 0 0 1 ${cx + rx} ${yb}`} className="qv-figure__cache" />
                    <line x1={cx} y1={yh} x2={cx + rx} y2={yh} className="qv-figure__trait" />
                    <circle cx={cx} cy={yh} r={2.5} className="qv-figure__point" />
                    {L.r && (rx < 45
                        ? <text x={cx + rx + 6} y={yh + 4} className="qv-figure__cote">{L.r}</text>
                        : <text x={cx + rx / 2} y={yh - 6} textAnchor="middle" className="qv-figure__cote">{L.r}</text>)}
                    {L.h && <text x={cx + rx + 8} y={(yh + yb) / 2 + 4} className="qv-figure__cote">{L.h}</text>}
                </g>
            );
        }
        default:
            return null;
    }
}

// Échelle exprimée en unités de dessin : longueur rapportée au même facteur que la figure.
function facteur(d, extraBas) {
    const pts = d.shape === 'rect' ? [[0, 0], [d.w, d.h]]
        : d.shape === 'right_triangle' ? [[0, 0], [d.base, d.height]]
        : d.shape === 'triangle' ? [[0, 0], [d.base, d.height]]
        : d.shape === 'polygon' ? (d.points || []) : null;
    return pts && pts.length ? cadre(pts, extraBas).k : null;
}

export default function Figure({ data }) {
    if (!data?.shape) return null;
    const echelle = data.scale_bar && typeof data.scale_bar.length === 'number' ? data.scale_bar : null;
    const extraBas = echelle ? 26 : 0;
    const k = echelle ? facteur(data, extraBas) : null;
    const figure = dessin(data, extraBas);
    if (!figure) return null;
    return (
        <figure className="qv-figure">
            {data.title && <figcaption className="qv-figure__title">{data.title}</figcaption>}
            <svg viewBox={`0 0 ${W} ${H}`} className="qv-figure__svg" role="img" aria-label={data.title || 'Figure géométrique'}>
                {figure}
                {echelle && k && (
                    <g className="qv-figure__echelle">
                        <line x1={MARGE_X} y1={H - 12} x2={MARGE_X + echelle.length * k} y2={H - 12} className="qv-figure__trait" />
                        <line x1={MARGE_X} y1={H - 16} x2={MARGE_X} y2={H - 8} className="qv-figure__trait" />
                        <line x1={MARGE_X + echelle.length * k} y1={H - 16} x2={MARGE_X + echelle.length * k} y2={H - 8} className="qv-figure__trait" />
                        <text x={MARGE_X + echelle.length * k + 8} y={H - 8} className="qv-figure__cote">{echelle.label}</text>
                    </g>
                )}
            </svg>
            {data.note && <div className="qv-figure__note">{data.note}</div>}
        </figure>
    );
}
